import { User } from '@ravago/shared/authentication';
import RevalidationTag from '@ravago/shared/page-data/enums/revalidation-tags/tags';
import { ContentfulCollectionName, ContentfulPaginatedResponse, ContentfulProduct, ContentfulProductEntry } from '@ravago/shared/page-data/models/contentful';
import { BaseQuery } from '@ravago/shared/page-data/models/utils';

import { ContentfulClientApi, Entry, EntryCollection } from 'contentful';

import { AssetUtils } from '../asset/asset.utils';
import { cache } from '../cache/cache.utils';
import { createClient } from '../contentful/client';
import { MarketSegmentUtils } from '../market-segment/market-segment.utils';
import { QueryBuilder } from '../query-builder/query-builder';

/* c8 ignore start */
export class ContentfulProductUtils {
  static getProductCollection = cache(async (locale: string, query: BaseQuery, user: User | undefined, overwriteClientApi?: ContentfulClientApi): Promise<ContentfulPaginatedResponse<ContentfulProduct[]>> => {
    const contentfulClientApi = overwriteClientApi ?? createClient([RevalidationTag.products]);
    const contentType: ContentfulCollectionName = 'product';
    const { limit, select, page, search, orderBy, filterBy, include, productIds, producerIds, brandIds, processIds, industryIds, segmentIds } = query;

    const queryOptions = new QueryBuilder()
      .contentType(contentType)
      .limit(limit || 4)
      .skip((limit || 4) * (page && page > 0 ? page - 1 : 0))
      .search(search)
      .order(orderBy)
      .include(include || 10)
      .locale(locale)
      .setArrayOptional('sys.id[in]', productIds)
      .setArrayOptional('fields.brand.sys.id[in]', brandIds)
      .setOptional('fields.processes.sys.id', processIds?.[0]);

    if (!user) {
      queryOptions.set('fields.isConflicting[ne]', 'true');
    }

    if (select) {
      queryOptions.select(select);
    }

    if (producerIds?.length) {
      const [producerId] = producerIds;

      queryOptions.set('fields.brand.sys.contentType.sys.id', 'brand');
      queryOptions.set('fields.brand.fields.producer.sys.id', producerId);
    }

    if (industryIds?.length) {
      const marketSegments = await MarketSegmentUtils.getMarketSegmentsByQuery(
        locale,
        {
          limit: 1000,
          filterBy,
          orderBy,
          industryIds
        },
        overwriteClientApi
      );
      const marketSegmentIds: Array<string> = marketSegments?.map((marketSegment) => marketSegment.sys.id) || [];
      queryOptions.set('fields.industrySegments.sys.id[in]', marketSegmentIds.join(','));
    }

    if (segmentIds?.length) {
      const [segmentId] = segmentIds;

      queryOptions.set('fields.industrySegments.sys.id', segmentId);
    }

    const entries: EntryCollection<ContentfulProductEntry> = await contentfulClientApi.getEntries(queryOptions.build());
    const products = this.normalizeEntryCollection(entries).slice(0, limit || 4);
    return { items: products, total: entries.total };
  });

  static getProductByEntryId = cache(async (locale: string, entryId?: string, overwriteClientApi?: ContentfulClientApi): Promise<ContentfulProduct | undefined> => {
    const contentfulClientApi = overwriteClientApi ?? createClient([RevalidationTag.products]);

    if (!entryId) return undefined;

    try {
      const entry = await contentfulClientApi.getEntry<ContentfulProductEntry>(entryId, { locale, include: 10 });

      if (!entry) return undefined;

      return this.normalizeEntry(entry);
    } catch (err) {
      return undefined;
    }
  });

  private static normalizeEntryCollection({ items }: EntryCollection<ContentfulProductEntry>): ContentfulProduct[] {
    return items.map(this.normalizeEntry);
  }

  static normalizeEntry(entry: Entry<ContentfulProductEntry>): ContentfulProduct {
    return {
      id: entry.sys.id,
      name: entry.fields.name ?? '',
      slug: entry.fields.slug ?? '',
      grade: entry.fields.grade ?? '',
      ranking: entry.fields.ranking ?? 0,
      image: entry.fields.image ? { url: entry.fields.image.fields?.file?.url ?? '' } : undefined,
      brand: {
        sys: { id: entry.fields.brand?.sys.id ?? '' },
        seoMetadata: { noFollow: entry.fields.brand?.fields?.seoMetadata?.fields?.noFollow },
        slug: entry.fields.brand?.fields?.slug ?? '',
        name: entry.fields.brand?.fields?.name ?? '',
        excerpt: '',
        producer: {
          sys: { id: entry.fields.brand?.fields?.producer?.sys.id ?? '' },
          seoMetadata: { noFollow: entry.fields.brand?.fields?.producer?.fields?.seoMetadata?.fields?.noFollow },
          name: entry.fields.brand?.fields?.producer?.fields?.name ?? '',
          logo: { url: entry.fields.brand?.fields?.producer?.fields?.logo?.fields?.file?.url ?? '' },
          slug: entry.fields.brand?.fields?.producer?.fields?.slug ?? '',
          storefront: {
            route: entry.fields.brand?.fields?.producer?.fields?.storefront?.fields?.route ?? '',
            seoMetadata: { noFollow: entry.fields.brand?.fields?.producer?.fields?.storefront?.fields?.seoMetadata?.fields?.noFollow }
          }
        },
        countries:
          entry.fields.brand?.fields?.countries?.map((country) => ({
            name: country.fields?.name ?? '',
            isoCode: country.fields?.isoCode ?? ''
          })) ?? []
      },
      polymerGroup: entry.fields.polymerSubgroup?.fields?.group?.fields?.name ?? '',
      polymerSubGroup: entry.fields.polymerSubgroup?.fields?.name ?? '',
      segments:
        entry.fields.industrySegments?.map((industrySegment) => ({
          sys: { id: industrySegment.sys.id },
          image: AssetUtils.mapToImage(industrySegment.fields?.image),
          name: industrySegment.fields?.name ?? '',
          description: industrySegment.fields?.description ?? '',
          applications: industrySegment.fields?.applications ?? [],
          rank: industrySegment.fields?.rank ?? 0,
          industry: {
            name: industrySegment.fields?.industry?.fields?.name ?? '',
            slug: industrySegment.fields?.industry?.fields?.slug ?? '',
            id: industrySegment.fields?.industry?.sys.id ?? '',
            keyAreas: industrySegment.fields?.industry?.fields?.keyAreas ?? [],
            callToActionType: industrySegment.fields?.industry?.fields?.callToActionType ?? 'Off',
            image: {
              url: industrySegment.fields?.industry?.fields?.image?.fields?.file?.url ?? '',
              title: industrySegment.fields?.industry?.fields?.image?.fields?.title ?? ''
            },
            seoMetadata: {
              noFollow: industrySegment.fields?.industry?.fields?.seoMetadata?.fields?.noFollow
            }
          }
        })) ?? [],
      fillers:
        entry.fields.fillers?.map((filler) => ({
          filler: { id: filler.sys.id, name: filler.fields?.filler.fields?.name ?? '' },
          percentage: filler.fields?.percentage ?? 0
        })) ?? [],
      processes:
        entry.fields.processes?.map((process) => ({
          id: process.sys.id,
          name: process.fields?.name ?? '',
          seoMetadata: {
            noFollow: process.fields?.seoMetadata?.fields?.noFollow
          },
          slug: process.fields?.slug ?? ''
        })) ?? [],
      features:
        entry.fields.features?.map((featureEntry) => ({
          id: featureEntry.sys.id,
          name: featureEntry.fields?.name || ''
        })) ?? [],
      specifications:
        entry.fields.specifications?.map((specification) => ({
          name: specification.fields?.name ?? '',
          provider: specification.fields?.provider?.fields?.name ? { name: specification.fields.provider.fields.name, type: specification.fields.provider.fields.type } : undefined,
          variation: specification.fields?.variation ?? ''
        })) ?? [],
      form: entry.fields.form?.fields?.name ?? '',
      isPreferred: entry.fields.isPreferred ?? false,
      quality: { id: entry.fields.quality?.sys.id ?? '', name: entry.fields.quality?.fields.name ?? '' },
      ulPortfolio: entry.fields.ulPortfolio ?? false,
      ulProductId: entry.fields.ulProductId || undefined,
      modifiedAt: entry.fields.modifiedAt ? new Date(entry.fields.modifiedAt) : new Date(),
      sustainabilitySubGroup: entry.fields.sustainability?.fields
        ? {
            id: entry.fields.sustainability.sys.id,
            name: entry.fields.sustainability.fields.name,
            sustainabilityGroup: entry.fields.sustainability.fields?.sustainabilityGroup?.fields
              ? {
                  id: entry.fields.sustainability.fields.sustainabilityGroup.sys.id,
                  name: entry.fields.sustainability.fields.sustainabilityGroup.fields?.name ?? ''
                }
              : undefined,
            labelIconPair: entry.fields.sustainability.fields?.labelIconPair?.fields
          }
        : undefined,
      density: entry.fields.density?.map((density) => ({
        value: density.fields.value ?? density.fields.density ?? '',
        testingMethod: density.fields?.testingMethod,
        unit: density.fields.unit ?? 'SI'
      })),
      // TODO: remove fallback to other fields (temporary solution due to backwards compatibility with old entries and elastic) should be changed to just fields.value in the future.
      flowRate: entry.fields.flowRate?.map((flow) => ({
        value: flow.fields.value ?? flow.fields.flowRate ?? '',
        testingMethod: flow.fields.testingMethod,
        condition: flow.fields.condition,
        unit: flow.fields.unit ?? 'SI'
      })),
      flameRating: entry.fields.flameRating?.map((flame) => ({
        value: flame.fields.value ?? flame.fields.flameRating ?? '',
        testingMethod: flame.fields.testingMethod,
        condition: flame.fields.condition,
        unit: flame.fields.unit ?? 'SI'
      })),
      notchedImpact: entry.fields.notchedImpact?.map((impact) => ({
        value: impact.fields.value ?? impact.fields.strength ?? '',
        testingMethod: impact.fields.testingMethod,
        condition: impact.fields.condition,
        unit: impact.fields.unit ?? 'SI'
      })),
      seoMetadata: { noFollow: entry.fields.seoMetadata?.fields?.noFollow }
    };
  }
}

/* c8 ignore end */
